<template>
    <div
        class="explore-listings mg-container"
        :class="[theme]"
        v-if="keywords && keywords.length === 1"
    >
        <div>
            <h2 class="mg-title">
                {{
                    $t(
                        level === 3
                            ? 'md-search-result.exploreListingsNear'
                            : level > 0
                                ? 'md-search-result.exploreListingsIn'
                                : 'md-search-result.exploreListings',
                        {
                            name: lastLevel.name,
                            interpolation: { escapeValue: false }
                        }
                    )
                }}
            </h2>
            <ul class="tags" v-if="cmpTags.length">
                <li v-for="item in cmpTags" :key="item.kee">
                    <a :href="getUrl(item)" rel="noopener noreferrer">{{
                        item.label
                    }}</a>
                </li>
            </ul>
            <ul class="tags" v-if="priceList.length">
                <li v-for="item in priceList" :key="item.key">
                    <a :href="item.link" rel="noopener noreferrer">{{
                        item.label
                    }}</a>
                </li>
            </ul>
            <ol class="lists">
                <li v-for="(item, index) in lists" :key="index">
                    <a :href="item.link" rel="noopener noreferrer">
                        <span class="label">{{ item.title }}</span>
                        <span class="price">{{ item.price }}</span>
                    </a>
                </li>
            </ol>
        </div>
    </div>
</template>

<script>
import { mapState as rootMapState } from 'pinia';
import { usePageStore } from '@/store';
import { normalizeParams } from './_center-adaptor/normalizeParams';
import util from '@/common/util';
import { searchRealTimeListings } from '@/common/search-request';
import { buildUrl } from '@/router/index.js';

export default {
    i18next: {
        ns: 'md-search-result'
    },
    props: ['theme', 'breadCrumbs', 'keywords', 'averageData'],
    computed: {
        ...rootMapState(usePageStore, {
            'isSearchCenter': store => store.page.searchCenter,
            'currencySymbol': store => store.currency.symbol,
            'currencyRate': store => store.currencyRate,
            'page': store => store.page
        }),
        location() {
            return this.keywords?.[0] || {};
        },
        level() {
            return this.breadCrumbs && this.breadCrumbs.length;
        },
        lastLevel() {
            return [...this.breadCrumbs].pop() || {};
        },
        avearagePrice() {
            if (!this.averageData?.avgResultList) {
                return []
            }
            return this.averageData?.avgResultList?.find(item => item.key === 'price') || {};
        },
        priceList() {
            if (
                /\d+/.test(this.avearagePrice.value) &&
                Number(this.avearagePrice.value)
            ) {
                const baseCurrency = util.transferPriceToDollar({
                    price: this.avearagePrice.value,
                    isInt: true,
                    currencyRate: this.currencyRate
                });
                return [
                    {
                        label: this.$t('md-search-result.lessThan', {
                            num: this.formatPrice(
                                this.avearagePrice.value * 0.8
                            )
                        }),
                        link: this.getPriceLink(
                            `,${
                                parseInt(
                                    (baseCurrency * 0.8) / 1000
                                ) * 1000
                            }`
                        ),
                        key: 1
                    },
                    {
                        label: `${this.formatPrice(
                            this.avearagePrice.value * 0.8
                        )} - ${this.formatPrice(
                            this.avearagePrice.value * 1.2
                        )}`,
                        link: this.getPriceLink(
                            `${
                                parseInt(
                                    (baseCurrency * 0.8) / 1000
                                ) * 1000
                            },${
                                parseInt(
                                    (baseCurrency * 1.2) / 1000
                                ) * 1000
                            }`
                        ),
                        key: 2
                    },
                    {
                        label: this.$t('md-search-result.over', {
                            num: this.formatPrice(
                                this.avearagePrice.value * 1.2
                            )
                        }),
                        link: this.getPriceLink(
                            `${
                                parseInt(
                                    (baseCurrency * 1.2) / 1000
                                ) * 1000
                            }`
                        ),
                        key: 3
                    }
                ];
            }
            return [];
        },
        cmpTags() {
            return [
                {
                    label: this.$t('md-search-result.openHouse1'),
                    kee: 'hasOpenHouse-1-listingStatus-Active'
                },
                {
                    label: this.$t('md-search-result.priceReduce'),
                    kee: 'hasPriceChanged-3'
                },
                {
                    label: this.$t('md-search-result.virtualTour'),
                    kee: 'VirtualTour-Flag'
                },
                {
                    label: this.$t('md-search-result.3dTour'),
                    kee: '3DTour-Flag'
                }
            ];
        }
    },
    data: function() {
        return {
            lists: []
        };
    },
    methods: {
        getPriceLink(value) {
            const query = this.paramsFactory({
                key: this.location.keyword,
                keywordType: this.location.keywordType,
                'minPrice-maxPrice': value
            });
            return buildUrl({ name: 'listing', query: query });
        },
        getUrl(item) {
            const { kee } = item;
            const query = this.paramsFactory({
                key: this.location.keyword,
                keywordType: this.location.keywordType,
                selected: kee
            });
            return buildUrl({ name: 'listing', query: query });
        },
        paramsFactory(params) {
            return normalizeParams(
                this.isSearchCenter,
                params,
                {},
                { needKey: true }
            );
        },
        formatPrice(num) {
            if (num >= 1000 && num < 1000000) {
                return this.currencySymbol + parseInt(num / 1000) + 'K';
            } else if (num >= 1000000) {
                return this.currencySymbol + (num / 1000000).toFixed(1) + 'M';
            } else {
                return this.currencySymbol + num;
            }
        },
        getData() {
            const base = {
                key: this.location.keyword,
                keywordType: this.location.keywordType,
                'purchaseType-For-Sale-Rent': '1'
            };
            const linkPramas = [
                this.paramsFactory(
                    { ...base, 'More-Than-Beds': '1,1' },
                    {},
                    { needKey: true }
                ),
                this.paramsFactory(
                    { ...base, 'More-Than-Beds': '2,2' },
                    {},
                    { needKey: true }
                ),
                this.paramsFactory(
                    { ...base, 'More-Than-Beds': '3,3' },
                    {},
                    { needKey: true }
                ),
                this.paramsFactory(
                    { ...base, 'More-Than-Beds': '4' },
                    {},
                    { needKey: true }
                )
            ];
            const requests = linkPramas.map(params =>
                searchRealTimeListings.call(this, {
                    params: {
                        ...params,
                        statTypes: 'PRICE_STATS',
                        cache: true,
                        timezone: this.getCookie('timezone') || util.getTimeZone()
                    },
                    type: 'neighborStats',
                    globalData: this.page
                }));
            Promise.all(requests).then(res => {
                this.lists = res
                    .map((item, index) => {
                        let { statsResults } =
                            (item.statsResults ? item : item.data) || {};
                        if (!statsResults) {
                            return { counts: 0 }; 
                        }
                        let { min, max, count } =
                            statsResults.statResultList[0];
                        min = this.formatPrice(min);
                        max = this.formatPrice(max);
                        let price = min === max ? min : `${min} - ${max}`;
                        return {
                            title: this.$t('md-search-result.homesWithBed', {
                                homeCount: count,
                                bedCount:
                                    index === 3 ? index + 1 + '+' : index + 1,
                                count: index + 1
                            }),
                            price,
                            counts: count,
                            link: buildUrl({ name: 'listing', query: linkPramas[index] }) 
                        };
                    })
                    .filter(item => item.counts > 0);
            });
        }
    },
    watch: {
        keywords: {
            handler: function(newVal) {
                if (newVal && newVal.length) {
                    this.getData();
                }
            },
            deep: true
        }
    },
    beforeMount() {
        this.getData();
    }
};
</script>

<style lang="scss">
@import '../../theme/media.scss';
@import '../../theme/mixin.scss';
// broker
.explore-listings.broker-listing-list,
.explore-listings.broker-listing-map,
.explore-listings.mg-container.broker-listing-market-insight {
    .tags {
        li {
            border: none;
            height: 40px;
            border-radius: 5px;
            background-color: #f3f3f3;
            &:hover {
                border: 1px solid var(--g-btn-background);
                background-color: #fff;
                box-shadow: 0 2px 5px 0 rgba(0, 10, 30, 0.05);
                a {
                    color: var(--g-btn-background);
                }
            }
            a {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: center;
                font: 14px var(--font-normal);
                color: var(--color-title);
            }
        }
    }
    .lists {
        li {
            border-radius: 5px;
            background: #f3f3f3;
            padding: 0 15px;
            margin-bottom: 15px;
            border: none;
            &:last-child {
                margin-bottom: 0;
            }
            &:hover {
                color: var(--g-btn-background);
                border: 1px solid var(--g-btn-background);
                box-shadow: 0 2px 5px 0 rgba(0, 10, 30, 0.05);
                background: #fff;
            }
        }
    }
}
// template2
.explore-listings.template1-listing-list,
.explore-listings.template2-listing-list {
    background: #fff;

    .mg-title {
        color: #000000;
    }
    .tags li {
        border: solid 1px #e1e2e6;
        color: #797e8b;
        font-family: var(--font-normal);
        height: 35px;
        line-height: 35px;
        &:hover {
            color: #1a88ff;
            border-color: #1a88ff;
            border-radius: 2px;
            box-shadow: 0 1px 5px 0 rgba(0, 10, 30, 0.1);
        }
    }
    .lists {
        @include media-800 {
            li {
                display: flex;
            }
        }
        @include media-min-800 {
            li {
                width: calc((100% - 35px) / 2);
                display: inline-flex;
                margin-right: 35px;
                &:nth-child(2n) {
                    margin-right: 0;
                }
            }
        }
        li {
            color: #797e8b;
            border: 1px solid #e1e2e6;
            margin-bottom: 20px;
            font-family: var(--font-normal);
            height: 35px;
            line-height: 25px;
            a {
                padding: 10px 15px;
            }
            &:last-child {
                margin-bottom: 0;
            }
            &:hover {
                border: 1px solid #1a88ff;
                color: #1a88ff;
            }
        }
    }
}
.explore-listings.template1-listing-map,
.explore-listings.template2-listing-map {
    background: #fff;
    .mg-title {
        color: #000000;
    }
    .tags li {
        border: solid 1px #e1e2e6;
        color: #797e8b;
        font-family: var(--font-normal);
        height: 35px;
        line-height: 35px;
        &:hover {
            color: #1a88ff;
            border-color: #1a88ff;
            border-radius: 2px;
            box-shadow: 0 1px 5px 0 rgba(0, 10, 30, 0.1);
        }
    }
    .lists li {
        color: #797e8b;
        border: 1px solid #e1e2e6;
        display: flex;
        margin-bottom: 20px;
        font-family: var(--font-normal);
        height: 35px;
        line-height: 35px;
        a {
            padding: 10px 15px;
        }
        &:last-child {
            margin-bottom: 0;
        }
        &:hover {
            border: 1px solid #1a88ff;
            color: #1a88ff;
        }
    }
}
.explore-listings.template1-listing-market-insight,
.explore-listings.template2-listing-market-insight {
    background: #fff;
    .mg-title {
        color: #000000;
    }
    .tags li {
        border: solid 1px #e1e2e6;
        color: #797e8b;
        font-family: var(--font-normal);
        &:hover {
            color: #1a88ff;
            border-color: #1a88ff;
            border-radius: 2px;
            box-shadow: 0 1px 5px 0 rgba(0, 10, 30, 0.1);
        }
    }

    .lists li {
        color: #797e8b;
        border: 1px solid #e1e2e6;
        display: flex;
        font-family: var(--font-normal);
        margin-bottom: 20px;
        a {
            padding: 10px 15px;
        }
        &:last-child {
            margin-bottom: 0;
        }
        &:hover {
            border: 1px solid #1a88ff;
            color: #1a88ff;
        }
    }
}
// template3
.explore-listings.template3-listing-list {
    background: #fff;
    .mg-title {
        font-size: var(--mg-title-size);
        font-weight: bold;
        color: #000;
    }
    .tags {
        li {
            font-family: var(--font-normal);
            border: solid 1px #d6dae8;
            color: #8b93a7;
            &:hover {
                color: #03c9ff;
            }
        }
    }
    .lists {
        margin-top: -16px;
        @include media-800 {
            li {
                display: flex;
            }
        }
        @include media-min-800 {
            li {
                width: calc((100% - 35px) / 2);
                display: inline-flex;
                margin-right: 35px;
                &:nth-child(2n) {
                    margin-right: 0;
                }
            }
        }
        li {
            color: #8b93a7;
            border-bottom: 1px solid #d6dae8;
            &:hover {
                color: #03c9ff;
            }
            a {
                padding: 30px 0 15px;
            }
        }
    }
}
.explore-listings.template3-listing-map {
    background: #fff;
    .mg-title {
        font-weight: bold;
        font-size: var(--mg-title-size);
        color: #000000;
    }
    .tags li {
        border: solid 1px #e1e2e6;
        color: #797e8b;
        &:hover {
            color: #03c9ff;
        }
    }
    .lists {
        margin-top: -16px;
    }
    .lists li {
        color: #797e8b;
        border-bottom: 1px solid #e1e2e6;
        display: flex;
        &:hover {
            color: #03c9ff;
        }
        a {
            padding: 30px 0 15px;
        }
    }
}
.explore-listings.template3-listing-market-insight {
    background: #fff;
    .mg-title {
        font-size: var(--mg-title-size);
        color: #000;
    }
    .tags li {
        border: solid 1px #d6dae8;
        color: #8b93a7;
        &:hover {
            color: #03c9ff;
        }
    }
    .lists {
        margin-top: -16px;
    }
    .lists li {
        color: #8b93a7;
        border-bottom: 1px solid #d6dae8;
        display: flex;
        &:hover {
            color: #03c9ff;
        }
        a {
            padding: 30px 0 15px;
        }
    }
}
// template4
.explore-listings.template4-listing-list {
    background: #fff;
    .mg-title {
        font-size: var(--mg-title-size);
        color: #4a4a60;
        font-weight: 900;
        margin-bottom: 30px;
    }
    .tags li {
        height: 30px;
        line-height: 30px;
        border: solid 1px #d6dae8;
        color: #8b93a7;
        &:hover {
            color: #4c84ff;
        }
    }
    .lists {
        @include media-800 {
            li {
                display: flex;
            }
        }
        @include media-min-800 {
            li {
                width: calc((100% - 35px) / 2);
                display: inline-flex;
                margin-right: 35px;
                &:nth-child(2n) {
                    margin-right: 0;
                }
            }
        }
        li {
            color: #8b93a7;
            border-bottom: 1px solid #d6dae8;
            &:hover {
                color: #4c84ff;
            }
            a {
                padding: 25px 0 15px;
            }
        }
    }
}
.explore-listings.template4-listing-map {
    background: #242a3d;
    .mg-title {
        font-weight: 900;
        color: #fff;
        font-size: var(--mg-title-size);
        margin-bottom: 30px;
    }
    .tags li {
        border: solid 1px #3f4965;
        color: #8195bd;
        margin: 0 10px 15px 0;
        &:hover {
            color: #4c84ff;
        }
    }
    .lists li {
        color: #8195bd;
        border-bottom: 1px solid #3f4965;
        display: flex;
        &:hover {
            color: #4c84ff;
        }
    }
}
.explore-listings.template4-listing-market-insight {
    background: #fff;
    .mg-title {
        font-weight: bold;
        color: #4a4a60;
        font-size: var(--mg-title-size);
    }
    .tags li {
        height: 30px;
        line-height: 30px;
        border: solid 1px #d6dae8;
        color: #8b93a7;
        &:hover {
            color: #4c84ff;
        }
    }
    .lists li {
        color: #8b93a7;
        border-bottom: 1px solid #d6dae8;
        display: flex;
        &:hover {
            color: #4c84ff;
        }
        a {
            padding: 25px 0 15px;
        }
    }
}

.explore-listings.mg-container:not(.template4-listing-list, .template4-listing-map, .template4-listing-market-insight) {
    @media (max-width: 600px) {
        width: 100%;
        margin-left: 0;
        margin-right: 0;
    }
}
.explore-listings.mg-container.template4-listing-list,
.explore-listings.mg-container.template4-listing-map,
.explore-listings.mg-container.template4-listing-market-insight {
    @media (max-width: 600px) {
        width: calc(100% - 30px);
        margin-left: 15px;
        margin-right: 15px;
    }
}
.explore-listings {
    margin-top: 40px;
    margin-bottom: 40px;
    background: #fff;
    @include media-800 {
        .mg-title {
            font-size: var(--mg-title-size-mobile) !important;
        }
    }

    @media (max-width: 600px) {
        margin: 20px 0;
    }

    .mg-title {
        margin-bottom: 20px;
        padding-bottom: 0 !important;
        text-transform: uppercase;
        white-space: normal !important;
    }
    .tags {
        @include media-min-800 {
            display: flex;
            flex-direction: row;
            justify-content: space-between;
            li {
                flex: 1;
            }
        }
        @include media-800 {
            li {
                display: inline-block;
                width: calc((100% - 10px) / 2);
                &:nth-child(2n) {
                    margin-right: 0;
                }
            }
        }
        li {
            height: 30px;
            font-size: 14px;
            line-height: 30px;
            border: 1px solid #e1e2e6;
            text-align: center;
            margin: 0 10px 16px 0;
            color: #797e8b;
            @include ellipsis();
            font-family: var(--font-normal);
            a {
                display: inline-block;
                width: 100%;
                height: 100%;
                padding: 0 10px;
                cursor: pointer;
            }
            &:hover {
                color: #03c9ff;
            }
            &:last-child {
                margin-right: 0;
            }
        }
    }
    .lists {
        li {
            border-bottom: solid 1px #e1e2e6;
            color: #515666;
            font-size: 14px;
            font-family: var(--font-normal);
            a {
                cursor: pointer;
                display: inline-block;
                width: 100%;
                height: 100%;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
                align-items: center;
                padding: 14px 0;
                font-family: var(--font-normal);
            }
            &:hover {
                color: #03c9ff;
            }
            .label {
                flex: 1;
                margin-right: 20px;
                @include ellipsis();
            }
        }
    }
}
</style>
